import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { clearPlanIntent } from '../components/PlanIntentBanner';
import { trackPurchase } from '../utils/analytics';
import * as TrialAPI from '../api/Trial';
import { getStateByID } from '../constants/statesData';

interface SubStatus {
  plan: 'free' | 'solo' | 'pro' | 'team';
  tier?: 'micro' | 'starter' | 'firm' | 'practice';
  status: string;
  billingInterval?: 'monthly' | 'annual';
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  solo: 'Solo',
  pro: 'Pro',
  team: 'Team / Firm',
};

const TIER_LABELS: Record<string, string> = {
  micro: 'Micro',
  starter: 'Starter',
  firm: 'Firm',
  practice: 'Practice',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-success',
  trialing: 'bg-info',
  past_due: 'bg-danger',
  canceled: 'bg-secondary',
  incomplete: 'bg-warning',
};

const BillingSettings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sub, setSub] = useState<SubStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchSuccess, setSwitchSuccess] = useState('');
  const [error, setError] = useState('');
  const [trial, setTrial] = useState<TrialAPI.TrialStatus | null>(null);
  const [primaryChoice, setPrimaryChoice] = useState<string>('');
  const [savingPrimary, setSavingPrimary] = useState(false);
  const [primarySaved, setPrimarySaved] = useState(false);

  const success = searchParams.get('success') === 'true';
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    (async () => {
      try {
        if (success) {
          clearPlanIntent();
          const syncUrl = sessionId
            ? `/api/billing/sync?session_id=${encodeURIComponent(sessionId)}`
            : '/api/billing/sync';
          let synced: any = null;
          for (let attempt = 0; attempt < 5; attempt++) {
            try {
              const syncRes = await api.get(syncUrl);
              if (syncRes.data?.synced && syncRes.data?.plan !== 'free') {
                synced = syncRes.data;
                break;
              }
            } catch (_) {}
            if (attempt < 4) await new Promise(r => setTimeout(r, 1500));
          }
          if (synced) {
            trackPurchase(synced.plan, synced.billingInterval ?? 'monthly', sessionId, synced.tier);
          }
        }
        const r = await api.get('/api/billing/status');
        setSub(r.data);

        if (r.data?.status === 'trialing') {
          const t = await TrialAPI.getTrialStatus();
          setTrial(t);
          if (t.chosenProfessionID && t.chosenStateID != null) {
            setPrimaryChoice(`${t.chosenProfessionID}|${t.chosenStateID}`);
          } else if (t.stateOptions.length > 0) {
            setPrimaryChoice(`${t.stateOptions[0].professionID}|${t.stateOptions[0].stateID}`);
          }
        }
      } catch {
        setError('Failed to load billing information.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSavePrimary = async () => {
    if (!primaryChoice) return;
    const [professionID, stateIDStr] = primaryChoice.split('|');
    setSavingPrimary(true);
    setPrimarySaved(false);
    try {
      await TrialAPI.setTrialPrimary(professionID, Number(stateIDStr));
      setPrimarySaved(true);
    } catch {
      setError('Could not save your choice. Please try again.');
    } finally {
      setSavingPrimary(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/api/billing/create-portal-session');
      window.location.href = data.url;
    } catch {
      setError('Could not open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You\'ll keep access until the end of the current billing period.')) return;
    setCancelLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/billing/cancel');
      setSub(prev => prev ? { ...prev, cancelAtPeriodEnd: true } : prev);
      alert(data.message || 'Subscription scheduled for cancellation.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/billing/reactivate');
      setSub(prev => prev ? { ...prev, cancelAtPeriodEnd: false } : prev);
      alert(data.message || 'Subscription reactivated.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reactivate subscription. Please try again.');
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleSwitchToAnnual = async () => {
    const savings = sub?.plan === 'pro'          ? 24
      : sub?.tier === 'micro'   ? 120
      : sub?.tier === 'starter' ? 240
      : sub?.tier === 'firm'    ? 360
      : sub?.tier === 'practice' ? 600
      : 24;
    if (!window.confirm(`Switch to annual billing? You'll save $${savings}/year. Your remaining monthly days are prorated as a credit on your next invoice.`)) return;
    setSwitchLoading(true);
    setError('');
    setSwitchSuccess('');
    try {
      const { data } = await api.post('/api/billing/switch-interval');
      setSub(prev => prev ? { ...prev, billingInterval: 'annual', currentPeriodEnd: data.currentPeriodEnd } : prev);
      setSwitchSuccess(data.message || 'Switched to annual billing.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to switch billing interval. Please try again.');
    } finally {
      setSwitchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const isProOrTeam = sub?.plan === 'solo' || sub?.plan === 'pro' || sub?.plan === 'team';
  const isTrialing = sub?.status === 'trialing';
  // "Manage Billing & Invoices" / "Cancel Subscription" only make sense for a
  // real paid Stripe subscription — a trial has neither, so it gets its own section.
  const isPaidActive = isProOrTeam && !isTrialing;
  const planLabel = isTrialing ? 'Pro (Trial)' : sub?.plan === 'team' && sub?.tier
    ? `${PLAN_LABELS['team']} — ${TIER_LABELS[sub.tier]}`
    : PLAN_LABELS[sub?.plan || 'free'];

  return (
    <div className="container mt-4" style={{ maxWidth: 680 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Billing & Subscription</h3>
        <Link to="/profile" className="btn btn-outline-secondary btn-sm">← Profile</Link>
      </div>

      {success && sub && (
        <div className="alert alert-success">
          🎉 Subscription activated! Welcome to {sub.plan !== 'free' ? planLabel : 'Pro'}.
        </div>
      )}
      {switchSuccess && <div className="alert alert-success">{switchSuccess}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Current plan card */}
      <div className="card shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 className="mb-1">Current Plan</h5>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold" style={{ fontSize: '1.4rem' }}>
                  {planLabel}
                </span>
                {sub?.status && (
                  <span className={`badge ${STATUS_BADGE[sub.status] || 'bg-secondary'}`}>
                    {sub.status}
                  </span>
                )}
              </div>
            </div>
            {isPaidActive && (
              <div className="text-end text-muted small">
                {sub?.billingInterval === 'annual' ? 'Annual billing' : 'Monthly billing'}
                {sub?.currentPeriodEnd && (
                  <div>
                    {sub.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} on{' '}
                    {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                )}
              </div>
            )}
          </div>

          {sub?.cancelAtPeriodEnd && (
            <div className="alert alert-warning py-2 mb-3 d-flex justify-content-between align-items-center">
              <span>Your subscription will cancel at the end of the current period.</span>
              <button
                className="btn btn-sm btn-warning ms-3"
                onClick={handleReactivate}
                disabled={reactivateLoading}
              >
                {reactivateLoading ? 'Reactivating…' : 'Reactivate'}
              </button>
            </div>
          )}

          {/* Annual upsell — only for active monthly subscribers */}
          {isPaidActive && sub?.billingInterval === 'monthly' && !sub?.cancelAtPeriodEnd && (
            <div className="d-flex align-items-center justify-content-between p-3 mb-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div>
                <div className="fw-semibold small" style={{ color: '#166534' }}>
                  Switch to Annual — save{' '}
                  ${sub.plan === 'pro' ? 12 : sub.plan === 'solo' ? 12 : sub.tier === 'micro' ? 72 : sub.tier === 'starter' ? 48 : sub.tier === 'firm' ? 24 : sub.tier === 'practice' ? 600 : 72}/yr
                </div>
                <div className="small text-muted">
                  {sub.plan === 'pro'
                    ? '$4/mo billed annually vs. $5/mo monthly'
                    : sub.plan === 'solo'
                    ? '$2/mo billed annually vs. $3/mo monthly'
                    : sub.tier === 'micro'   ? '$24/mo annually vs. $30/mo monthly'
                    : sub.tier === 'starter' ? '$36/mo annually vs. $40/mo monthly'
                    : sub.tier === 'firm'    ? '$48/mo annually vs. $50/mo monthly'
                    : sub.tier === 'practice' ? '$199/mo annually vs. $249/mo monthly'
                    : 'Annual billing saves you money'}
                  . Unused days prorated as a credit.
                </div>
              </div>
              <button
                className="btn btn-sm fw-semibold ms-3 flex-shrink-0"
                style={{ background: '#16a34a', color: '#fff', whiteSpace: 'nowrap' }}
                onClick={handleSwitchToAnnual}
                disabled={switchLoading}
              >
                {switchLoading ? 'Switching…' : 'Switch to Annual'}
              </button>
            </div>
          )}

          <hr />

          {isPaidActive ? (
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-primary"
                onClick={openPortal}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening…' : '↗ Manage Billing & Invoices'}
              </button>
              {!sub?.cancelAtPeriodEnd ? (
                <button
                  className="btn btn-outline-danger"
                  onClick={handleCancel}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Canceling…' : 'Cancel Subscription'}
                </button>
              ) : (
                <button
                  className="btn btn-outline-success"
                  onClick={handleReactivate}
                  disabled={reactivateLoading}
                >
                  {reactivateLoading ? 'Reactivating…' : 'Reactivate Subscription'}
                </button>
              )}
            </div>
          ) : isTrialing ? (
            <div>
              <p className="mb-1">
                <strong>{trial?.daysLeft ?? '—'} day{trial?.daysLeft === 1 ? '' : 's'} left</strong> in your Pro trial
                {trial?.trialEndsAt && <> — ends {new Date(trial.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>}.
              </p>
              <p className="text-muted small mb-3">
                No action needed to keep exploring. If you don't upgrade, you'll automatically move to the Free plan when
                the trial ends — one license stays active (your choice below), the rest are safely deactivated, never deleted.
                Upgrading at any time keeps everything active with no interruption.
              </p>

              {trial && trial.stateOptions.length > 1 && (
                <div className="border rounded p-3 mb-3" style={{ background: '#f8fafc' }}>
                  <label className="form-label fw-semibold small mb-2">
                    Which license should stay active if you land on the Free plan?
                  </label>
                  <div className="d-flex flex-column gap-1 mb-2">
                    {trial.stateOptions.map(opt => {
                      const state = getStateByID(opt.stateID);
                      const value = `${opt.professionID}|${opt.stateID}`;
                      return (
                        <label key={value} className="d-flex align-items-center gap-2 small" style={{ cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="trialPrimary"
                            value={value}
                            checked={primaryChoice === value}
                            onChange={() => { setPrimaryChoice(value); setPrimarySaved(false); }}
                          />
                          {opt.professionID} — {state?.name || `State ${opt.stateID}`} ({opt.cycleCount} cycle{opt.cycleCount !== 1 ? 's' : ''})
                        </label>
                      );
                    })}
                  </div>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleSavePrimary}
                    disabled={savingPrimary}
                  >
                    {savingPrimary ? 'Saving…' : primarySaved ? '✓ Saved' : 'Save Choice'}
                  </button>
                </div>
              )}

              <div className="d-flex gap-2 flex-wrap">
                <Link to="/pricing" className="btn btn-primary">
                  Upgrade to Pro — $5/mo
                </Link>
                <Link to="/pricing" className="btn btn-outline-primary">
                  Upgrade to Solo — $3/mo
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-muted mb-3">
                You're on the free plan. Upgrade to Pro to unlock unlimited professions, states, and compliance cycles.
              </p>
              <Link to="/pricing" className="btn btn-primary">
                View Pricing & Upgrade
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Feature summary */}
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <h6 className="mb-0">What's included in your plan</h6>
        </div>
        <div className="card-body p-0">
          <table className="table mb-0">
            <tbody>
              {[
                ['Professions & states',    sub?.plan === 'free' || sub?.plan === 'solo' ? '1 each'      : 'Unlimited'],
                ['Active cycles',           sub?.plan === 'free' ? '1'            : 'Unlimited'],
                ['Deadline email alerts',   'Included'],
                ['PDF compliance export',   'Included'],
                ['Company admin dashboard', sub?.plan === 'team' ? 'Included'     : 'Not included'],
              ].map(([feature, value]) => (
                <tr key={feature}>
                  <td className="text-muted small ps-3">{feature}</td>
                  <td className={`small fw-medium pe-3 text-end ${value === 'Not included' ? 'text-muted' : value === 'Included' || value === 'Unlimited' ? 'text-success' : ''}`}>
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upgrade nudge for free users */}
      {!isProOrTeam && (
        <div className="card border-0 text-white" style={{ background: 'var(--color-primary)' }}>
          <div className="card-body p-4 text-center">
            <h5 className="fw-bold mb-2">Upgrade to Pro — $5/mo or $48/yr</h5>
            <p className="mb-3" style={{ opacity: 0.8 }}>
              Unlock unlimited states, professions, and compliance cycles.
              Annual plan saves $12/yr — just $4/mo billed yearly.
            </p>
            <Link to="/pricing" className="btn fw-bold" style={{ background: '#f59e0b', color: '#000' }}>
              See Pricing
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;
